/**
 * Filtro de várias opções: botão com contagem e painel de caixas de marcar.
 *
 * Papel: reproduz o `.ms-dropdown` da origem de Manutenções (CSS l. 192–251,
 * comportamento l. 839–866) — botão "Prefixo: Todos/Nenhum/N de M", painel com
 * uma caixa por opção, e os links "Selecionar todos" e "Limpar".
 *
 * Como usar: o estado é de quem monta, por `useMultiSelecao`. O painel fecha com
 * clique em qualquer lugar fora do componente, e é isso que faz abrir uma
 * segunda multiseleção fechar a primeira, como na origem. O painel é posicionado
 * por `absolute` dentro do próprio componente, e não num portal: dentro de um
 * contêiner com `overflow:hidden` ele seria cortado, e a toolbar da origem não
 * tem esse recorte.
 */
import { useEffect, useId, useRef, useState } from 'react';

import { rotuloDaMultiSelecao } from './use-multi-selecao';

/** Uma opção do painel. */
export interface OpcaoDeSelecao {
  /** O valor que entra em `marcados`. */
  valor: string;
  /** O texto ao lado da caixa. */
  rotulo: string;
}

interface MultiSelecaoProps {
  /** O nome do filtro, como a origem o escreve no botão. */
  prefixo: string;
  /** As opções, na ordem em que aparecem no painel. */
  opcoes: readonly OpcaoDeSelecao[];
  /** O que está marcado, vindo de `useMultiSelecao`. */
  marcados: ReadonlySet<string>;
  /** Chamado com o conjunto novo a cada caixa ou link. */
  onMudar: (marcados: ReadonlySet<string>) => void;
}

/**
 * Uma multiseleção.
 *
 * @param prefixo - O nome do filtro no botão.
 * @param opcoes - As opções do painel.
 * @param marcados - As opções marcadas.
 * @param onMudar - Recebe o conjunto inteiro depois de cada mudança.
 * @returns O botão e, aberto, o painel.
 */
export function MultiSelecao({ prefixo, opcoes, marcados, onMudar }: MultiSelecaoProps) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const idDoPainel = useId();
  const marcadas = opcoes.filter((opcao) => marcados.has(opcao.valor)).length;

  useEffect(() => {
    if (!aberto) return;

    /** Fecha o painel quando o clique cai fora do componente. */
    function handleCliqueNoDocumento(evento: MouseEvent) {
      if (evento.target instanceof Node && raiz.current?.contains(evento.target)) return;
      setAberto(false);
    }

    document.addEventListener('click', handleCliqueNoDocumento);
    return () => document.removeEventListener('click', handleCliqueNoDocumento);
  }, [aberto]);

  /**
   * Marca ou desmarca uma opção.
   *
   * @param valor - O valor da opção.
   * @param marcado - O estado novo da caixa.
   */
  function handleAlternar(valor: string, marcado: boolean) {
    const novos = new Set(marcados);
    if (marcado) novos.add(valor);
    else novos.delete(valor);
    onMudar(novos);
  }

  /** Marca todas as opções do painel. */
  function handleSelecionarTodos() {
    onMudar(new Set([...marcados, ...opcoes.map((opcao) => opcao.valor)]));
  }

  /** Desmarca todas as opções do painel. */
  function handleLimpar() {
    const novos = new Set(marcados);
    for (const opcao of opcoes) novos.delete(opcao.valor);
    onMudar(novos);
  }

  return (
    <div ref={raiz} className="relative inline-block">
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls={aberto ? idDoPainel : undefined}
        onClick={() => setAberto((atual) => !atual)}
        className="cursor-pointer rounded-[6px] border border-emb-borda bg-superficie px-2.5 py-2 font-outfit text-[12px] font-normal text-texto-fraco hover:bg-superficie-2"
      >
        {rotuloDaMultiSelecao(prefixo, opcoes.length, marcadas)}
      </button>

      {aberto && (
        <div
          id={idDoPainel}
          role="group"
          aria-label={prefixo}
          className="absolute top-[calc(100%+4px)] left-0 z-30 min-w-[240px] rounded-[6px] border border-emb-borda bg-superficie px-2.5 py-2 shadow-emb-painel"
        >
          <div className="max-h-[230px] overflow-y-auto pr-1">
            {opcoes.map((opcao) => (
              <label
                key={opcao.valor}
                className="flex cursor-pointer items-center gap-[7px] px-0.5 py-[5px] font-outfit text-[12px] font-normal whitespace-nowrap"
              >
                <input
                  type="checkbox"
                  className="[margin:revert]"
                  checked={marcados.has(opcao.valor)}
                  onChange={(evento) => handleAlternar(opcao.valor, evento.target.checked)}
                />
                {opcao.rotulo}
              </label>
            ))}
          </div>
          <div className="mt-1.5 flex gap-3 border-t border-emb-borda pt-1.5">
            <button
              type="button"
              onClick={handleSelecionarTodos}
              className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[11px] text-brass-light underline"
            >
              Selecionar todos
            </button>
            <button
              type="button"
              onClick={handleLimpar}
              className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[11px] text-brass-light underline"
            >
              Limpar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
